import { useDispatch } from 'react-redux';
import { useSelector } from 'react-redux';
import { RootState } from 'src/redux';
import {
    messageReceived,
    webSocketConnected,
    webSocketDisconnected,
    resetMessages,
} from 'src/redux/Websocket/webScoketSlice';

/**
 * Custom hook for managing WebSocket connections and messages.
 *
 * This hook provides functionality to connect and disconnect from a WebSocket,
 * handle incoming messages, and manage the connection state.
 *
 * @returns {Object} An object containing:
 * @returns {boolean} connected - A boolean indicating whether the WebSocket is connected.
 * @returns {Array} messages - An array of messages received through the WebSocket.
 * @returns {function} handleConnected - A function to dispatch the action for WebSocket connection.
 * @returns {function} handleDisconnected - A function to dispatch the action for WebSocket disconnection.
 * @returns {function} handleMessageReceived - A function to dispatch the action for handling received messages.
 * @returns {function} resetMessagesWS - A function to reset the received messages.
 * @returns {function} waitForConnection - A function that returns a promise which resolves when the WebSocket is connected, or rejects if the connection times out.
 *
 * @example
 * const MyComponent = () => {
 *   const {
 *     connected,
 *     messages,
 *     handleConnected,
 *     handleDisconnected,
 *     handleMessageReceived,
 *     resetMessagesWS,
 *     waitForConnection,
 *   } = useWebsocket();
 *
 *   useEffect(() => {
 *     waitForConnection().then(() => {
 *       console.log('WebSocket connected!');
 *     }).catch(error => {
 *       console.error(error);
 *     });
 *   }, []);
 *
 *   // Handle incoming messages
 *   const onMessage = (event: MessageEvent) => {
 *     const data = JSON.parse(event.data);
 *     handleMessageReceived(data);
 *   };
 *
 *   return (
 *     <div>
 *       <h1>WebSocket Status: {connected ? 'Connected' : 'Disconnected'}</h1>
 *       <button onClick={handleConnected}>Connect</button>
 *       <button onClick={handleDisconnected}>Disconnect</button>
 *       <button onClick={resetMessagesWS}>Reset Messages</button>
 *       <div>
 *         <h2>Messages:</h2>
 *         {messages.map((message, index) => (
 *           <p key={index}>{message}</p>
 *         ))}
 *       </div>
 *     </div>
 *   );
 * };
 */
const useWebsocket = () => {
    const dispatch = useDispatch();
    const { connected, messages } = useSelector((state: RootState) => state.webSocket);

    // Use websocket connected
    const handleConnected = () => {
        dispatch(webSocketConnected());
    };

    // Use websocket disconnected
    const handleDisconnected = () => {
        dispatch(webSocketDisconnected());
    };

    // Use message received
    const handleMessageReceived = (data: any) => {
        dispatch(messageReceived(data));
    };

    // Reset messages
    const resetMessagesWS = () => {
        dispatch(resetMessages());
    };

    /**
     * Wait for the WebSocket connection to be established.
     *
     * This function returns a promise that resolves when the WebSocket is connected,
     * or rejects if the connection times out after a specified period.
     *
     * @returns {Promise<void>} A promise that resolves when the WebSocket is connected.
     * @throws {Error} An error if the connection times out.
     */
    function waitForConnection(): Promise<void> {
        dispatch({ type: 'webSocket/connect' });
        handleDisconnected();
        return new Promise((resolve, reject) => {
            const timeout = 10000; // 10 seconds
            const start = Date.now();

            const interval = setInterval(() => {
                if (connected) {
                    console.log(connected, 'websocket: ' + connected + localStorage.getItem('connectionId'));
                    clearInterval(interval);
                    resolve(); // Successful resolution
                } else if (Date.now() - start > timeout) {
                    console.log('Timeout. Unable to establish connection.');
                    clearInterval(interval);
                    reject(new Error('Timeout. Unable to establish connection.'));
                }
            }, 1000); // Check every second
        });
    }

    return {
        connected,
        messages,
        handleConnected,
        handleDisconnected,
        handleMessageReceived,
        resetMessagesWS,
        waitForConnection,
    };
};

export default useWebsocket;
